import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../../models/user';

// New interfaces to match your backend
export interface PaginatedUsersResponse {
  users: User[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface UpdateUserRequest {
  username: string;
  password?: string;
  firstName: string;
  lastName: string;
  email?: string;
  role: 'admin' | 'user' | 'shopee_docs' | 'ShopeeChecker';
  isActive: boolean;
}

export interface RegisterRequest {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email?: string;
  role: 'admin' | 'user' | 'shopee_docs' | 'ShopeeChecker';
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  
  private apiUrl = `${environment.apiUrl}/user/admin-only`

  constructor(private http: HttpClient) { }

  // Backend serializes anonymous-type user objects as PascalCase (no global naming policy).
  // Normalise to camelCase so the User interface and templates can read them.
  getUsers(page: number = 1, pageSize: number = 20): Observable<PaginatedUsersResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<PaginatedUsersResponse>(`${this.apiUrl}/users`, { params }).pipe(
      map(response => {
        const rawUsers = (response.users ?? []) as unknown as Record<string, unknown>[];
        const users: User[] = rawUsers.map(u => ({
          id:               (u['id']              ?? u['Id'])              as number,
          username:         (u['username']        ?? u['Username'])        as string,
          firstName:        (u['firstName']       ?? u['FirstName'])       as string,
          lastName:         (u['lastName']        ?? u['LastName'])        as string,
          email:            (u['email']           ?? u['Email'])           as string | undefined,
          role:             (u['role']            ?? u['Role'])            as string,
          isActive:         (u['isActive']        ?? u['IsActive'])        as boolean,
          createdAtUtc:     (u['createdAtUtc']    ?? u['CreatedAtUtc'])    as string,
          lastUpdatedAtUtc: (u['lastUpdatedAtUtc'] ?? u['LastUpdatedAtUtc']) as string,
          createdBy:        (u['createdBy']       ?? u['CreatedBy'])       as string | undefined,
          lastModifiedBy:   (u['lastModifiedBy']  ?? u['LastModifiedBy'])  as string | undefined,
        }));
        return { ...response, users };
      })
    );
  }

  getUserById(id: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/users/${id}`);
  }

  createUser(userData: RegisterRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/register`, userData);
  }

  updateUser(id: number, userData: UpdateUserRequest): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/users/${id}`, userData);
  }

  deleteUser(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/users/${id}`)
  }

  toggleUserStatus(id: number): Observable<{message: string, isActive: boolean}> {
    return this.http.patch<{message: string, isActive: boolean}>(`${this.apiUrl}/users/${id}/toggle-active`, {});
  }

  // Security-related methods
  lockUser(id: number, reason: string): Observable<{message: string}> {
    return this.http.post<{message: string}>(`${this.apiUrl}/users/${id}/lock`, { reason });
  }

  unlockUser(id: number): Observable<{message: string}> {
    return this.http.post<{message: string}>(`${this.apiUrl}/users/${id}/unlock`, {});
  }
}
